import { useState, useCallback, useEffect } from 'react';
import NotificationToast, { ToastNotification } from './NotificationToast';

export interface NotificationContainerProps {}

let notificationId = 0;

export default function NotificationContainer() {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const addNotification = useCallback((notification: Omit<ToastNotification, 'id'>) => {
    const id = `notification-${++notificationId}`;
    setNotifications(prev => [...prev, { ...notification, id }]);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Listen for browser push notifications when app is in foreground
  useEffect(() => {
    console.log('🎯 NotificationContainer mounted - toast system ready');

    // Listen for visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 App became visible, ready to show notification toasts');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for custom notification events (can be triggered by service workers or other code)
    const handleCustomNotification = (event: CustomEvent) => {
      console.log('🔔 Custom notification event received:', event.detail);

      const message = event.detail.body || event.detail.message || '';
      const title = event.detail.title || 'New Notification';

      addNotification({
        title,
        message,
        type: event.detail.type || 'info',
        duration: event.detail.duration || 5000,
      });
    };

    window.addEventListener('chime-notification' as any, handleCustomNotification as any);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('chime-notification' as any, handleCustomNotification as any);
    };
  }, [addNotification]);

  // Expose global method for showing notifications
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).showNotification = addNotification;
      console.log('✅ window.showNotification() is now available');
    }
  }, [addNotification]);

  return (
    <div className="fixed top-16 right-4 z-50 space-y-2 max-w-sm w-full pointer-events-none">
      <div className="space-y-2 pointer-events-auto">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={removeNotification}
          />
        ))}
      </div>
    </div>
  );
}